import type { ReactNode } from "react";

import type { VehicleControl } from "@/api/client";
import { ago } from "@/lib/format";

/** What `vehicle_control_status` (field 68) says the vehicle is doing.
 *
 *  THE HERO OF THE LIVE WINDOW. The page leads with board IDs and GPS, and the
 *  question an operator actually opens it to answer is whether the bike in
 *  front of them can move. That is one fact, and the skill's rule for a single
 *  headline is a hero figure — except this headline is a STATE, not a
 *  magnitude, so it takes a hero bar rather than a number. Exactly one per
 *  view.
 *
 *  NO BIT ARITHMETIC IN THIS FILE. The layout lives in the firmware's C and in
 *  `shared/lib/vehicle_control.py`; the server hands over the decoded block.
 *  A third copy in the browser is how a renumbered bit ends up looking right
 *  on screen while reporting a restraint that moved.
 *
 *  REPORTS, NEVER JUDGES (D-115). The age of the reading sits beside it and no
 *  verdict is attached: five minutes is unremarkable for a bike in a depot and
 *  alarming for one mid-ride, and only the person looking knows which.
 *
 *  STATUS COLOUR NEVER CARRIES MEANING ALONE. Measured, not assumed: this
 *  console's `--ok` and `--warn` are ΔE 4.1 apart under protanopia, so a
 *  red-green colourblind operator cannot tell them apart. Every state below
 *  ships an icon AND a word, and the two that matter ship a whole sentence.
 */

type Tone = { col: string; soft: string; line: string };
const TONE: Record<string, Tone> = {
  ok: { col: "var(--ok)", soft: "var(--ok-soft)", line: "var(--ok-line)" },
  warn: { col: "var(--warn)", soft: "var(--warn-soft)", line: "var(--warn-line)" },
  bad: { col: "var(--bad)", soft: "var(--bad-soft)", line: "var(--bad-line)" },
  mute: { col: "var(--t-mute)", soft: "var(--well)", line: "var(--line2)" },
};

/** The four readings, each with the sentence it needs.
 *
 *  `armed` is its own state and not a shade of "immobilised", because it is
 *  the one an operator must not misread: the vehicle has the command, has NOT
 *  actuated, and can still move. That distinction cost this product a live
 *  console lie, so it gets the longest sentence on the page. */
function headline(vc: VehicleControl): { icon: string; word: string; said: string; tone: Tone } {
  if (vc.malformed) {
    return {
      icon: "!", word: "Reading looks wrong", tone: TONE.bad,
      said: `${vc.malformed}. The device should not be able to report this, `
          + `so it is not interpreted; it wants a look at the firmware.`,
    };
  }
  if (vc.armed) {
    return {
      icon: "◌", word: `${vc.commanded_name} armed`, tone: TONE.warn,
      said: "The vehicle has the command and has NOT actuated it yet. "
          + "It is waiting to come to a halt. IT CAN STILL MOVE.",
    };
  }
  if (vc.is_applied) {
    return {
      icon: "■", word: `Immobilised · ${vc.applied_name}`, tone: TONE.bad,
      said: vc.deciding_level_name
        ? `In force on the vehicle, decided by ${vc.deciding_level_name} `
          + `(level ${vc.deciding_level}).`
        : "In force on the vehicle.",
    };
  }
  if (vc.restrained) {
    // THE READING THAT USED TO FALL THROUGH TO A GREEN "MOBILE".
    //
    // The vehicle is enforcing something, but not what it was asked for. It is
    // not `armed` (it has actuated) and not `is_applied` (not the right
    // action), and with only those two tests it landed on "nothing commanded
    // and nothing enforced — free to move" over a bike that was already
    // freewheeling. Found by the server-side test for `armed` before it
    // shipped; the card needed the same fourth state the decoder did.
    return {
      icon: "■", word: `Immobilised · ${vc.applied_name}`, tone: TONE.bad,
      said: `The vehicle is enforcing ${vc.applied_name}, but it was asked for `
          + `${vc.commanded_name}. It is restrained, though not with the action `
          + `Orbit requested. Worth a look: only another command at that level `
          + `changes it.`,
    };
  }
  return {
    icon: "▶", word: "Mobile", tone: TONE.ok,
    said: "Nothing commanded and nothing enforced. The vehicle is free to move.",
  };
}

/** A boolean the device reported, as an icon + the WHOLE PHRASE for that state.
 *
 *  Each state carries its own full text rather than a shared label plus an
 *  on/off suffix. Dattatray named these, and the reason the composed version
 *  was wrong is that the three flags do not share a vocabulary: two are
 *  ignition sources that are ON or OFF, and the third is a phone that is
 *  NEARBY or AWAY. A `label + onWord` template forced all three into one
 *  shape and produced "phone linked", which reads as a property of the phone
 *  rather than where it is.
 *
 *  Never colour alone: this console's `--ok` and `--warn` are ΔE 4.1 apart
 *  under protanopia, so the icon and the words are what carry the state. */
function Flag({ on, onText, offText, hint }: {
  on: boolean; onText: string; offText: string; hint: string;
}) {
  const t = on ? TONE.ok : TONE.mute;
  return (
    <span className="chip" title={hint}
      style={{ color: t.col, background: t.soft, fontSize: 10.5, cursor: "help" }}>
      {on ? "●" : "○"} {on ? onText : offText}
    </span>
  );
}

function Line({ k, v }: { k: string; v: ReactNode }) {
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "baseline", minWidth: 0 }}>
      <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.5,
                     textTransform: "uppercase", color: "var(--t-mute)",
                     flex: "0 0 92px" }}>{k}</span>
      <span style={{ fontSize: 12.5, color: "var(--t-body)", minWidth: 0 }}>{v}</span>
    </div>
  );
}

export function VehicleControlCard({ vc }: { vc: VehicleControl | null }) {
  // THE AGE COMES FROM THE WORD, NOT FROM THE DEVICE.
  //
  // This took `reportedAt` and the page passed `last_seen` — when the device
  // last spoke about ANYTHING. Field 68 is a condition the device re-asserts,
  // so a packet carrying only an SoC change left a ten-minute-old restraint
  // reading sitting under "as reported 1 second ago". `vc.reported_at` is the
  // word's own clock (`vcs_at`, 0086).
  const reportedAt = vc?.reported_at ?? null;
  // NOT THE SAME AS "nothing is held", and the difference matters. A device on
  // older firmware never sends field 68, and rendering that as a green
  // "Mobile" would invent the most reassuring possible answer out of silence.
  if (!vc) {
    return (
      <div className="panel panel-pad" style={{ marginBottom: 14, borderLeft: "3px solid var(--line2)" }}>
        <div style={{ fontSize: 12.5, fontWeight: 800, letterSpacing: 1,
                      textTransform: "uppercase", color: "var(--t-bright)" }}>
          Vehicle Control
        </div>
        <div style={{ fontSize: 12.5, color: "var(--t-mute)", marginTop: 6 }}>
          ○ Unknown. This device has never reported <span className="mono">vehicle_control_status</span>.
          That is older firmware, <strong>not</strong> a vehicle with nothing held:
          Orbit cannot see what this one is enforcing.
        </div>
      </div>
    );
  }

  const h = headline(vc);

  return (
    <div className="panel" style={{ marginBottom: 14, overflow: "hidden",
                                    borderTop: `3px solid ${h.tone.col}` }}>
      {/* ---- the hero bar: the whole bar carries the state ---- */}
      <div style={{ background: h.tone.soft, borderBottom: `1px solid ${h.tone.line}`,
                    padding: "14px 18px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 1,
                         textTransform: "uppercase", color: "var(--t-mute)" }}>
            Vehicle Control
          </span>
          <span style={{ marginLeft: "auto", fontSize: 11.5, color: "var(--t-mute)" }}>
            {/* THE STALENESS, WITHOUT A VERDICT. A state on screen with no age
                beside it reads as "now". */}
            as reported {reportedAt ? `${ago(reportedAt)} ago` : "(never)"}
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 8 }}>
          <span aria-hidden style={{ fontSize: 26, lineHeight: 1, color: h.tone.col }}>{h.icon}</span>
          {/* Proportional figures, not tabular: this is a standalone headline,
              and the skill reserves tabular-nums for columns. */}
          {/* The raw word has no row of its own any more, at Dattatray's
              call, and rightly: nobody reads a bitfield off a dashboard. It
              stays HERE, in the hover, because it is still the number to
              quote in a bug report and keeping it costs no space. */}
          <span title={`vehicle_control_status = ${vc.word} (0x${vc.word.toString(16)})`}
            style={{ fontSize: 25, fontWeight: 800, color: "var(--t-bright)",
                     lineHeight: 1.1, letterSpacing: -0.3, cursor: "help" }}>
            {h.word}
          </span>
        </div>
        <div style={{ fontSize: 12.5, color: "var(--t-body)", marginTop: 6, maxWidth: "68ch" }}>
          {h.said}
        </div>
      </div>

      {/* ---- the facts under it ---- */}
      <div style={{ padding: "13px 18px 15px", display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(270px, 1fr))", gap: "10px 24px" }}>
        <Line k="Commanded" v={<>
          {vc.commanded_name}
          {vc.commanded_name !== vc.applied_name && (
            <span className="muted"> · enforcing {vc.applied_name}</span>
          )}
        </>} />
        <Line k="Levels held" v={
          vc.levels_named.length
            ? <span style={{ display: "inline-flex", gap: 5, flexWrap: "wrap" }}>
                {vc.levels_named.map((n, i) => (
                  <span key={vc.levels_held[i]} className="chip"
                    title={`Level ${vc.levels_held[i]}: ${
                      vc.levels_held[i] === vc.deciding_level
                        ? "the highest open level, so its type is the one in force (OFL-06k)"
                        : "holding, but a higher level decides the type"}`}
                    style={{ fontSize: 10.5, cursor: "help",
                             color: vc.levels_held[i] === vc.deciding_level ? "var(--bad)" : "var(--t-mute)",
                             background: vc.levels_held[i] === vc.deciding_level ? "var(--bad-soft)" : "var(--well)" }}>
                    {n} ({vc.levels_held[i]})
                    {vc.levels_held[i] === vc.deciding_level ? " · decides" : ""}
                  </span>
                ))}
              </span>
            // JUST "none". The explanation was carrying the same meaning the
            // row label already gives — "Levels held: none" needs nothing
            // after it, and the sentence only widened the row.
            // `.muted` rather than `color: var(--t-dim)` inline:
            // `test_the_extracted_inline_styles_have_not_come_back` guards
            // exactly this, because the class was extracted FROM these.
            : <span className="muted"
                title="The vehicle reports no level holding it">none</span>
        } />
        <Line k="Ignition" v={
          // STACKED, one per line, at Dattatray's call. Three chips in a row
          // wrapped unpredictably once each carried its whole phrase
          // ("Physical Ignition OFF" is four times the width of "key off"),
          // so the set reflowed between one and three lines as the window
          // moved. A column is the same shape at every width, and these read
          // as a list of three independent facts rather than a sentence.
          <span style={{ display: "inline-flex", flexDirection: "column",
                         alignItems: "flex-start", gap: 4 }}>
            <Flag on={vc.physical_ignition}
              onText="Physical Ignition ON" offText="Physical Ignition OFF"
              hint="The physical ignition line (bit 0)" />
            <Flag on={vc.ble_ignition}
              onText="BLE Ignition ON" offText="BLE Ignition OFF"
              hint="Ignition asserted over BLE by the rider's app (bit 1)" />
            {/* NOT AN IGNITION SOURCE, and the wording has to keep that
                clear. This bit says a phone is in BLE range; it does not
                start the vehicle. "Phone Nearby" beside two ON/OFF ignition
                chips reads as proximity, which is what it is. */}
            <Flag on={vc.ble_connected}
              onText="Phone Nearby" offText="Phone Away"
              hint="A phone is connected over BLE right now (bit 2). This is proximity, not ignition." />
          </span>
        } />
        <Line k="Seat" v={
          vc.seat
            ? <span className="chip" title="Seat lock state (bits 7-8)"
                style={{ fontSize: 10.5, color: "var(--t-mute)", background: "var(--well)" }}>
                {vc.seat === "locked" ? "▪ locked" : "▫ unlocked"}
              </span>
            : <span className="muted" title="Neither seat bit is set: no seat lock fitted, or firmware that does not report one">
                not reported
              </span>
        } />
      </div>
    </div>
  );
}
